import type { ParsedUrlQuery } from "querystring";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getOrderDetailsV2, getPolicyStatus } from "@tm/insurance/api";
import { getPartnerOnClientSide } from "@tm/insurance/utils";
import type { OrderDetailsV2ApiResponse } from "../types/api";
import { normalizeOrderDetailsV2 } from "../utils/normalize";

// ── Types ───────────────────────────────────────────────────────────────

interface PollingConfig {
  isEnabled: boolean;
  intervalMs: number;
  durationMs?: number;
}

interface UsePolicyPollingOptions {
  /** Raw order data from the server/props */
  data: OrderDetailsV2ApiResponse;
  /** router.query from Next.js useRouter */
  routerQuery: ParsedUrlQuery;
}

interface UsePolicyPollingResult {
  /** Current order data (may have been updated by polling) */
  orderData: OrderDetailsV2ApiResponse;
  /** Setter for external updates (e.g. after endorsement save) */
  setOrderData: Dispatch<SetStateAction<OrderDetailsV2ApiResponse>>;
  /** Manually refetch the full order details */
  refreshOrderDetails: () => Promise<void>;
}

// ── Hook ────────────────────────────────────────────────────────────────

export function usePolicyPolling({
  data,
  routerQuery,
}: UsePolicyPollingOptions): UsePolicyPollingResult {
  const [orderData, setOrderData] = useState(data);

  // Derive polling config from current state — NOT the initial prop.
  // This ensures polling reacts to state updates (e.g. after refreshOrderDetails).
  const normalized = useMemo(
    () => normalizeOrderDetailsV2(orderData),
    [orderData]
  );

  const bookingId = normalized.bookingId;

  const pollingConfig = normalized.pageData?.pollingConfig as
    | PollingConfig
    | undefined;
  const pollingEnabled = pollingConfig?.isEnabled ?? false;
  const pollingIntervalMs = pollingConfig?.intervalMs ?? 0;
  const pollingDurationMs = pollingConfig?.durationMs;

  const policyStatus =
    (normalized.pageData?.policyGenerationStatus as string | undefined) ?? null;

  // Ref keeps routerQuery always-fresh without it being an effect dep
  const routerQueryRef = useRef(routerQuery);
  routerQueryRef.current = routerQuery;

  // ── Polling lifecycle refs ────────────────────────────────────────
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const initialStatusRef = useRef<string | null>(null);
  const pollingStartTimeRef = useRef<number | null>(null);

  // ── Manual refresh ─────────────────────────────────────────────────

  const refreshOrderDetails = useCallback(async () => {
    const partner = getPartnerOnClientSide();
    const query = routerQueryRef.current;
    const updatedOrderData = await getOrderDetailsV2({
      partner,
      bookingId,
      query,
    });
    setOrderData(updatedOrderData);
  }, [bookingId]);

  // ── Polling effect ─────────────────────────────────────────────────
  // Deps are all primitives — no spurious re-runs from object identity.
  // The effect's cleanup (stopPolling) runs before every re-invocation,
  // so no duplicate-init guard ref is needed.

  useEffect(() => {
    if (!pollingEnabled || !bookingId || !pollingIntervalMs) {
      return;
    }

    initialStatusRef.current = policyStatus;
    isPollingRef.current = true;
    pollingStartTimeRef.current = null;

    const stopPolling = () => {
      isPollingRef.current = false;
      pollingStartTimeRef.current = null;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    const pollPolicyStatus = async () => {
      if (!isPollingRef.current) return;

      if (pollingStartTimeRef.current === null) {
        pollingStartTimeRef.current = Date.now();
      }

      try {
        const partner = getPartnerOnClientSide();
        const query = routerQueryRef.current;
        const result = await getPolicyStatus({
          partner,
          bookingId,
          query,
        });

        const newStatus = result?.data?.policyStatus;

        if (!newStatus) return;

        if (initialStatusRef.current !== newStatus) {
          stopPolling();

          try {
            const updatedOrderData = await getOrderDetailsV2({
              partner,
              bookingId,
              query,
            });
            setOrderData(updatedOrderData);
          } catch (error) {
            // silently ignore refetch errors
          }
        } else if (pollingDurationMs && pollingStartTimeRef.current !== null) {
          const elapsed = Date.now() - pollingStartTimeRef.current;
          if (elapsed >= pollingDurationMs) {
            stopPolling();
          }
        }
      } catch {
        stopPolling();
      }
    };

    pollPolicyStatus();
    pollingIntervalRef.current = setInterval(
      pollPolicyStatus,
      pollingIntervalMs
    );

    return stopPolling;
  }, [
    bookingId,
    pollingEnabled,
    pollingIntervalMs,
    pollingDurationMs,
    policyStatus,
  ]);

  return { orderData, setOrderData, refreshOrderDetails };
}
